import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Boton } from '../componentes/ui/basicos';
import { importeParaMirar } from '../lib/numero';
import type { MedioDePago, PagoDeVenta } from './useVenta';
import type { Importe } from '@actualgold/shared';

interface PagoRepartido extends PagoDeVenta {
  readonly calculado: Importe;
}

/**
 * Con qué se paga esta venta.
 *
 * ## Por qué una lista y no un botón
 *
 * Porque **el cliente paga con lo que tiene**: mitad en efectivo y el resto con tarjeta es el caso
 * de todos los días, y hasta acá había que elegir uno solo. El servidor lo aceptaba desde E2 —la
 * venta recorre los cobros, los suma y guarda una fila por medio—; lo que faltaba era la pantalla.
 *
 * ## Por qué el caso simple no cambió
 *
 * Con una sola forma no hay nada que tipear: el importe queda vacío y significa «todo». Los botones
 * de medio siguen ahí y hacen lo mismo que antes. **Dividir el pago es un clic extra sólo para
 * quien lo divide**, que es lo que corresponde: el mostrador cobra de una forma casi siempre.
 */
export function FormasDePago({
  medios,
  pagos,
  faltante,
  onElegirUnico,
  onAgregar,
  onCambiar,
  onQuitar,
}: {
  readonly medios: readonly MedioDePago[];
  readonly pagos: readonly PagoRepartido[];
  readonly faltante: Importe;
  readonly onElegirUnico: (medio: MedioDePago) => void;
  readonly onAgregar: (medio: MedioDePago) => void;
  readonly onCambiar: (
    clave: string,
    cambio: { readonly [K in keyof PagoDeVenta]?: PagoDeVenta[K] },
  ) => void;
  readonly onQuitar: (clave: string) => void;
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const dividido = pagos.length > 1;
  const unico = pagos[0];

  return (
    <>
      {dividido ? null : (
        <div className="grid grid-cols-2 gap-2">
          {medios.map((medio) => (
            <Boton
              key={medio}
              tono={unico?.medioDePago === medio ? 'principal' : 'secundario'}
              onClick={() => {
                onElegirUnico(medio);
              }}
            >
              {t(`venta.${medio}`)}
            </Boton>
          ))}
        </div>
      )}

      {dividido ? (
        <ul className="flex flex-col gap-2">
          {pagos.map((pago, indice) => (
            <li key={pago.clave} className="flex items-end gap-2">
              <label className="flex-1">
                <span className="sr-only">{t('venta.medioDePago')}</span>
                <select
                  className="w-full rounded-marco border border-borde bg-superficie px-2 py-1.5 text-cuerpo"
                  value={pago.medioDePago}
                  onChange={(evento) => {
                    onCambiar(pago.clave, { medioDePago: evento.target.value as MedioDePago });
                  }}
                >
                  {medios.map((medio) => (
                    <option key={medio} value={medio}>
                      {t(`venta.${medio}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="w-32">
                <span className="sr-only">{t('venta.importe')}</span>
                <input
                  inputMode="decimal"
                  aria-label={`${t('venta.importe')} ${String(indice + 1)}`}
                  // El calculado como marca de agua: la fila que no se tipea muestra lo que le va
                  // a tocar, así se ve el reparto sin tener que confirmarlo para enterarse.
                  placeholder={importeParaMirar(pago.calculado.aTexto(), i18n.language)}
                  className="numero w-full rounded-marco border border-borde bg-superficie px-2 py-1.5 text-right text-cuerpo"
                  value={pago.importe}
                  onChange={(evento) => {
                    onCambiar(pago.clave, { importe: evento.target.value });
                  }}
                />
              </label>
              <Boton
                tono="sutil"
                aria-label={t('venta.quitarForma')}
                onClick={() => {
                  onQuitar(pago.clave);
                }}
              >
                <X className="size-4" aria-hidden />
              </Boton>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-2 flex items-center justify-between gap-2">
        <Boton
          tono="sutil"
          onClick={() => {
            onAgregar('efectivo');
          }}
        >
          <Plus className="size-4" aria-hidden />
          {t('venta.dividirElPago')}
        </Boton>
        {/* Lo que falta, en rojo, y sólo cuando falta. Es el único número que impide cobrar, y
            verlo mientras se reparte evita descubrirlo al apretar el botón. */}
        {faltante.esCero() ? null : (
          <span className="text-cuerpo">
            {faltante.esPositivo() ? t('venta.falta') : t('venta.sobra')}:{' '}
            <span className="cifra font-semibold text-mal">
              {importeParaMirar(faltante.valorAbsoluto().aTexto(), i18n.language)}
            </span>
          </span>
        )}
      </div>
    </>
  );
}
